import { SignOutIcon } from "@phosphor-icons/react";
import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { AuthProvider, useAuth, useSession } from "./auth/AuthContext";
import { RealtimeProvider } from "./realtime/RealtimeProvider";
import { ToastProvider } from "./components/Toast";
import { ConfirmProvider } from "./components/Confirm";
import { ErrorState, Loading } from "./components/States";
import { AuthPage } from "./pages/AuthPage";
import { SetPasswordPage } from "./pages/SetPasswordPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { HomeScene } from "./scene/HomeScene";
import { SectionPanel } from "./scene/SectionPanel";
import { ThreatTakeover } from "./hud/ThreatTakeover";
import { RadioProvider } from "./radio/useRadio";

const CamerasPage = lazy(() => import("./pages/CamerasPage"));
const AlertsPage = lazy(() => import("./pages/AlertsPage"));
const DronesPage = lazy(() => import("./pages/DronesPage"));
const PilotPage = lazy(() => import("./pages/PilotPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AccountPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.AccountPage })));

export function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="boot">
          <Loading label="loading…" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/** A section as a floating panel over the scene. */
function Section({ title, children, wide }: { title: string; children: ReactNode; wide?: boolean }) {
  return (
    <SectionPanel title={title} wide={wide}>
      <Suspense fallback={<Loading label="loading…" />}>{children}</Suspense>
    </SectionPanel>
  );
}

/** Owners get Settings; members get Account. Each redirects to the other's home. */
function OwnerOnly({ children, else: other }: { children: ReactNode; else: string }) {
  const { isOwner } = useSession();
  const location = useLocation();
  return isOwner ? <>{children}</> : <Navigate to={other} replace state={location.state} />;
}
function MemberOnly({ children, else: other }: { children: ReactNode; else: string }) {
  const { isOwner } = useSession();
  const location = useLocation();
  return isOwner ? <Navigate to={other} replace state={location.state} /> : <>{children}</>;
}

function Gate() {
  const { state, retry, signOut } = useAuth();
  const location = useLocation();

  if (state.status === "loading") {
    return (
      <div className="boot">
        <Loading label="loading your site…" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="boot">
        <div className="boot__inner">
          <ErrorState title="Could not load your account" message={state.message} onRetry={retry} />
          <button type="button" className="btn" onClick={() => void signOut()}><SignOutIcon size={16} aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (state.status === "signed-out") {
    return (
      <Routes>
        <Route path="/" element={<AuthPage notice={state.notice} />} />
        <Route path="*" element={<Navigate to="/" replace state={{ from: location.pathname + location.search }} />} />
      </Routes>
    );
  }

  // A temporary password must be replaced before anything else loads.
  if (state.status === "password-required") {
    return <SetPasswordPage />;
  }

  const section = location.pathname.split("/")[1] ?? "";

  return (
    <RealtimeProvider token={state.token}>
      <RadioProvider token={state.token} meId={state.user.id}>
      <ThreatTakeover />
      <Routes>
        <Route
          path="/drones/:droneId"
          element={
            <Lazy>
              <PilotPage />
            </Lazy>
          }
        />
        <Route element={<HomeScene />}>
          <Route index element={null} />
          <Route path="command" element={<Navigate to="/" replace />} />
          <Route path="signup" element={<Navigate to="/" replace />} />
          <Route path="clips" element={<Navigate to="/cameras" replace />} />
          <Route
            path="cameras"
            element={
              <Section key={section} title="cameras" wide>
                <CamerasPage />
              </Section>
            }
          />
          <Route
            path="alerts"
            element={
              <Section key={section} title="alerts">
                <AlertsPage />
              </Section>
            }
          />
          <Route
            path="drones"
            element={
              <Section key={section} title="drones">
                <DronesPage />
              </Section>
            }
          />
          <Route path="buttons" element={<Navigate to="/settings/buttons" replace />} />
          <Route path="users" element={<Navigate to="/settings/users" replace />} />
          <Route
            path="settings/:tab?"
            element={
              <OwnerOnly else="/account">
                <Suspense fallback={null}>
                  <SettingsPage key={section} />
                </Suspense>
              </OwnerOnly>
            }
          />
          <Route
            path="account"
            element={
              <MemberOnly else="/settings/password">
                <Suspense fallback={null}>
                  <AccountPage key={section} />
                </Suspense>
              </MemberOnly>
            }
          />
          <Route path="*" element={<NotFoundPage key={section} />} />
        </Route>
      </Routes>
      </RadioProvider>
    </RealtimeProvider>
  );
}
